import React, { useEffect, useState } from "react";
import {
    ArrowRight, Server, ShieldCheck, Database, Plus, Pencil, Trash2,
    AlertCircle,
} from "lucide-react";
import { http, formatBytes, formatDuration } from "@/lib/api";

/**
 * Source → Policy → Destination tri-card layout.
 *
 * Visual reshape of the existing schedule detail data. Reads every
 * value off the same `schedule.job_template` object the legacy
 * <DetailRow>s did, plus the optional `policy` extension block. No
 * new API calls — pure presentation.
 */
export function SourcePolicyDestination({ schedule, agents, describeFreq }) {
    const t = schedule?.job_template || {};
    const policy = t.policy || {};
    const src = agents?.find((a) => a.id === t.source_agent_id);
    const dst = agents?.find((a) => a.id === t.dest_agent_id);
    return (
        <div className="space-y-2" data-testid="sched-spd">
            <Lozenge
                tint="#5b5ef0"
                icon={Server}
                label="Source"
                title={src?.name || (t.source_agent_id || "").slice(0, 8) || "—"}
                testId="sched-spd-source"
            >
                <DataLine label="Path" value={t.source_path} mono />
                {src?.os && <DataLine label="OS" value={src.os} />}
                {src?.status && <DataLine label="Status" value={src.status} />}
            </Lozenge>
            <StackArrow />
            <Lozenge
                tint="#f59e0b"
                icon={ShieldCheck}
                label="Policy"
                title={describeFreq ? describeFreq(schedule) : (schedule?.frequency || "—")}
                testId="sched-spd-policy"
            >
                <DataLine label="Type" value={t.migration_type || "—"} />
                {t.retries != null && <DataLine label="Retries" value={String(t.retries)} />}
                <DataLine
                    label="Transport"
                    value={[
                        t.compression ? "compress" : null,
                        t.encryption ? "encrypt" : null,
                    ].filter(Boolean).join(" + ") || "plain"}
                />
                {policy.max_runtime_minutes != null && (
                    <DataLine label="Max runtime" value={`${policy.max_runtime_minutes}m`} />
                )}
                {policy.retention_versions != null && (
                    <DataLine label="Keep last" value={`${policy.retention_versions} versions`} />
                )}
                {policy.change_tracking !== false && (
                    <DataLine label="Tracking" value="enabled" tint="emerald" />
                )}
            </Lozenge>
            <StackArrow />
            <Lozenge
                tint="#22c55e"
                icon={Database}
                label="Destination"
                title={dst?.name || (t.dest_agent_id || "").slice(0, 8) || "—"}
                testId="sched-spd-destination"
            >
                <DataLine label="Path" value={t.destination_path} mono />
                {dst?.os && <DataLine label="OS" value={dst.os} />}
                {dst?.status && <DataLine label="Status" value={dst.status} />}
                <DataLine label="Size" value={formatBytes(t.total_bytes || 0)} />
            </Lozenge>
        </div>
    );
}

function StackArrow() {
    return (
        <div className="flex items-center justify-center">
            <span className="flex h-5 w-5 items-center justify-center rounded-full border border-border bg-background/80 text-muted-foreground">
                <ArrowRight size={10} className="rotate-90" />
            </span>
        </div>
    );
}

function Lozenge({ tint, icon: Icon, label, title, testId, children }) {
    return (
        <div
            className="flex flex-col rounded-xl border bg-surface p-3.5 shadow-sm"
            style={{ borderColor: `${tint}40` }}
            data-testid={testId}
        >
            <div className="flex items-center gap-2">
                <span
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md"
                    style={{ background: `${tint}1f`, color: tint }}
                >
                    <Icon size={13} />
                </span>
                <div className="min-w-0">
                    <div
                        className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.22em]"
                        style={{ color: tint }}
                    >
                        {label}
                    </div>
                    <div className="truncate font-display text-sm font-semibold leading-tight" title={title}>
                        {title}
                    </div>
                </div>
            </div>
            <div className="mt-2.5 space-y-1 border-t border-border/60 pt-2.5">
                {children}
            </div>
        </div>
    );
}

function DataLine({ label, value, mono, tint }) {
    const valueCls = [
        mono ? "font-mono text-[11px]" : "text-[11.5px]",
        tint === "emerald" ? "text-emerald-400" : "text-foreground",
        "truncate text-right",
    ].join(" ");
    return (
        <div className="flex items-center justify-between gap-2 text-[11px]">
            <span className="shrink-0 text-muted-foreground">{label}</span>
            <span className={valueCls} title={String(value || "")}>{value || "—"}</span>
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────────
 * Change-tracking visualization. Calls the new
 *   GET /api/schedules/{id}/changes?limit=10
 * endpoint and renders an inline stacked bar chart of files
 * added / modified / deleted across the last N runs.
 * ────────────────────────────────────────────────────────────── */

export function ScheduleChangeTracking({ scheduleId }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!scheduleId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await http.get(`/schedules/${scheduleId}/changes?limit=10`);
                if (!cancelled) setData(data);
            } catch (e) {
                if (!cancelled) setErr(e?.response?.data?.detail || "Could not load change history.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [scheduleId]);

    if (loading) {
        return (
            <div className="rounded-lg border border-border bg-background/40 p-3 text-[10px] text-muted-foreground" data-testid="sched-changes-loading">
                Loading change history…
            </div>
        );
    }
    if (err) {
        return (
            <div className="flex items-center gap-1.5 rounded-lg border border-border bg-background/40 p-3 text-[10px] text-muted-foreground" data-testid="sched-changes-error">
                <AlertCircle size={11} /> {err}
            </div>
        );
    }
    const runs = data?.runs || [];
    const totals = data?.totals || { files_added: 0, files_modified: 0, files_deleted: 0, bytes_moved: 0 };

    if (runs.length === 0) {
        return (
            <div className="rounded-lg border border-dashed border-border bg-background/30 p-4 text-center text-[11px] text-muted-foreground" data-testid="sched-changes-empty">
                No completed runs yet — change tracking starts after the first successful run.
            </div>
        );
    }

    // Largest "total per run" determines bar scaling. Floor at 1 to avoid /0.
    const peak = Math.max(
        1,
        ...runs.map((r) => (r.files_added + r.files_modified + r.files_deleted)),
    );

    return (
        <div className="rounded-lg border border-border bg-background/40 p-3" data-testid="sched-changes">
            <div className="mb-2 flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Change tracking · last {runs.length} run{runs.length === 1 ? "" : "s"}
                </div>
                <div className="font-mono text-[10px] tabular-nums text-muted-foreground">
                    {formatBytes(totals.bytes_moved)} moved
                </div>
            </div>

            <div className="grid grid-cols-3 gap-2 pb-3">
                <ChangeStat
                    label="Added"
                    value={totals.files_added}
                    icon={Plus}
                    tint="#22c55e"
                    testId="sched-changes-added"
                />
                <ChangeStat
                    label="Modified"
                    value={totals.files_modified}
                    icon={Pencil}
                    tint="#00C2FF"
                    testId="sched-changes-modified"
                />
                <ChangeStat
                    label="Deleted"
                    value={totals.files_deleted}
                    icon={Trash2}
                    tint="#ef4444"
                    testId="sched-changes-deleted"
                />
            </div>

            {/* Stacked bar chart */}
            <div className="space-y-1 border-t border-border/60 pt-2">
                {runs.map((r) => {
                    const total = r.files_added + r.files_modified + r.files_deleted;
                    const widthPct = (total / peak) * 100;
                    return (
                        <div key={r.run_id} className="flex items-center gap-2" data-testid={`sched-changes-run-${r.run_id}`}>
                            <span className="w-20 shrink-0 truncate font-mono text-[9.5px] text-muted-foreground" title={r.run_time}>
                                {(r.run_time || "").slice(5, 16).replace("T", " ")}
                            </span>
                            <div
                                className="relative h-3 flex-1 overflow-hidden rounded-full bg-border/50"
                                title={`Added ${r.files_added} · Modified ${r.files_modified} · Deleted ${r.files_deleted}`}
                            >
                                <div
                                    className="flex h-full"
                                    style={{ width: `${widthPct}%` }}
                                >
                                    {r.files_added > 0 && (
                                        <span
                                            className="h-full"
                                            style={{
                                                width: `${(r.files_added / Math.max(1, total)) * 100}%`,
                                                background: "#22c55e",
                                            }}
                                        />
                                    )}
                                    {r.files_modified > 0 && (
                                        <span
                                            className="h-full"
                                            style={{
                                                width: `${(r.files_modified / Math.max(1, total)) * 100}%`,
                                                background: "#00C2FF",
                                            }}
                                        />
                                    )}
                                    {r.files_deleted > 0 && (
                                        <span
                                            className="h-full"
                                            style={{
                                                width: `${(r.files_deleted / Math.max(1, total)) * 100}%`,
                                                background: "#ef4444",
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                            <span className="w-16 shrink-0 text-right font-mono text-[10px] tabular-nums text-muted-foreground">
                                {total.toLocaleString()}
                            </span>
                            <span className="w-12 shrink-0 text-right font-mono text-[10px] tabular-nums text-muted-foreground">
                                {r.duration_ms != null ? formatDuration(r.duration_ms) : "—"}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function ChangeStat({ label, value, icon: Icon, tint, testId }) {
    return (
        <div className="rounded-md border border-border bg-surface p-2" data-testid={testId}>
            <div className="flex items-center gap-1.5">
                <span
                    className="inline-flex h-5 w-5 items-center justify-center rounded"
                    style={{ background: `${tint}1f`, color: tint }}
                >
                    <Icon size={10} />
                </span>
                <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                    {label}
                </span>
            </div>
            <div className="mt-1 font-display text-base font-bold tabular-nums" style={{ color: tint }}>
                {(value || 0).toLocaleString()}
            </div>
        </div>
    );
}
